import keyBy from 'lodash/keyBy';
import { JOB_POWERGRIDS_HISTORY_INTERVAL } from 'common/constants';
import { Logger } from 'common/logger';
import { checkWebsocketConnection } from 'common/saga-utils';
import { getSelectedParkData } from 'common/state.helper';

const defaultValue = {
    historicalData: { values: { active_power: { times: [] } } },
    forecastData: { active_power: { value: [] } },
    forecastHistoryData: { active_power: { value: [] } },
};

export const powerGrids = {
    name: 'powerGrids',
    state: {
        list: [],
        byId: {},
        historicalById: {},
        forecastById: {},
        forecastHistoryById: {},
        polling: null,
    },

    reducers: {
        loadMultiple(state, payload) {
            // Logger.debug(`PowerGrids.loadMultiple Bulk updating ${payload.length}`, payload);
            const turbinesWithKey = keyBy(payload, (o) => o.entityKey);
            return {
                ...state,
                byId: { ...state.byId, ...turbinesWithKey },
            };
        },
        updateAll(state, payload) {
            Logger.debug('PowerGrids.updateAll:', payload);
            return {
                ...state,
                byId: payload,
            };
        },
        setList(state, payload) {
            return {
                ...state,
                list: payload,
            };
        },
        updateAllHistorical(state, payload) {
            Logger.debug('PowerGrids.updateAllHistorical:', payload);
            return {
                ...state,
                historicalById: payload,
            };
        },
        updateAllForecast(state, payload) {
            Logger.debug('PowerGrids.updateAllForecast:', payload);
            return {
                ...state,
                forecastById: payload,
            };
        },
        updateAllForecastHistory(state, payload) {
            Logger.debug('PowerGrids.updateAllForecastHistory:', payload);
            return {
                ...state,
                forecastHistoryById: payload,
            };
        },
        loadOneHistory(state, payload) {
            Logger.debug('PowerGrids.loadOneHistory:', payload);
            return {
                ...state,
                historicalById: {
                    ...state.historicalById,
                    [payload.entityKey]: payload,
                },
            };
        },
        setForecastById(state, payload) {
            Logger.debug('PowerGrids.setForecastById:', payload);
            const { entityKey } = payload;
            return {
                ...state,
                forecastById: {
                    ...state.forecastById,
                    [entityKey]: payload,
                },
            };
        },
        setForecastHistoryById(state, payload) {
            Logger.debug('PowerGrids.setForecastHistoryById:', payload.item);
            const { entityKey } = payload.item;
            return {
                ...state,
                forecastHistoryById: {
                    ...state.forecastHistoryById,
                    [entityKey]: payload.item,
                },
            };
        },
        clearPolling(state) {
            if (state.polling) {
                clearInterval(state.polling);
                Logger.info('Polling stopped'); // Log when polling is stopped
            }
            return { ...state, polling: null };
        },
        setPolling(state, polling) {
            return { ...state, polling };
        },
    },

    effects: (dispatch) => ({
        async startHistoryJob() {
            Logger.info('Starting powerGrids.startHistoryJob');
            await checkWebsocketConnection();

            const historyJob = async () => {
                const parkData = getSelectedParkData();
                for (const entityKey of parkData.substations) {
                    dispatch({
                        type: 'webSocket/requestGridConnectionHistory',
                        payload: {
                            entityKey,
                            precision: 'minute',
                            count: 6 * 60,
                        },
                    });
                    dispatch({
                        type: 'webSocket/requestHistoricalProductionForecast',
                        payload: {
                            entityKey,
                            precision: 'minute',
                            count: 6 * 60,
                        },
                    });
                }
            };
            historyJob();
            // Start the polling
            const polling = setInterval(historyJob, JOB_POWERGRIDS_HISTORY_INTERVAL);
            dispatch.powerGrids.setPolling(polling);
        },
        clearHistoryJobs() {
            Logger.info('Stopping history job');
            dispatch.powerGrids.clearPolling(); // Clear polling when this action is dispatched
        },
        loadForecast(payload) {
            Logger.log('PowerGrids.loadForecast: ', payload);
            dispatch.powerGrids.setForecastById(payload);
        },

        loadForecastHistory(payload, rootState) {
            Logger.log('PowerGrids.loadForecastHistory: ', payload);
            const { selectedPark } = rootState.appData;
            dispatch.powerGrids.setForecastHistoryById({ item: payload, selectedPark });
        },

        reset() {
            Logger.info('Reset powerGrids');

            const parkData = getSelectedParkData();
            console.log('parkData', parkData);
            dispatch.powerGrids.clearHistoryJobs();

            const gridsWithDefaults = parkData.substations.map((entityKey) => ({
                entityKey,
            }));
            dispatch.powerGrids.loadMultiple(gridsWithDefaults);
            dispatch.powerGrids.setList(parkData.substations);

            const gridHistoryWithDefaults = parkData.substations.map((entityKey) => ({
                ...defaultValue.historicalData,
                historyKey: entityKey,
            }));
            const defaultHistoricalById = keyBy(gridHistoryWithDefaults, (o) => o.historyKey);

            dispatch.powerGrids.updateAllHistorical(defaultHistoricalById);

            const gridForecastWithDefaults = parkData.substations.map((entityKey) => ({
                ...defaultValue.forecastData,
                entityKey,
            }));
            const defaultForecastById = keyBy(gridForecastWithDefaults, (o) => o.entityKey);

            dispatch.powerGrids.updateAllForecast(defaultForecastById);
            dispatch.powerGrids.updateAllForecastHistory(defaultForecastById);
            dispatch.powerGrids.startHistoryJob();
        },

        restartJob() {
            Logger.info('Restarted powergrids');
        },
    }),
};

export default powerGrids;
